import {
  ref,
  shallowRef,
  watch,
  toValue,
  type Ref,
  type MaybeRefOrGetter,
} from "vue";
import { storeToRefs } from "pinia";
import { useSettingsStore } from "@/stores/settings.store";
import {
  MAP_ZOOM_DEFAULT,
  MAP_ZOOM_FALLBACK,
  MAP_ZOOM_MIN_RECENTER,
  US_CENTER,
} from "@/constants/map";

// Dark style matching app's blue/purple theme
const darkMapStyle: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#0d1117" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0d1117" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2937" }],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4b5563" }],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [{ color: "#374151" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2937" }],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#0f1520" }],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#1f2937" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b7280" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#0f172a" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4b5563" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#1e293b" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#0f172a" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b7280" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#2d3748" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1a202c" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca3af" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#1f2937" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b7280" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#0a1628" }],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#131e30" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3b6494" }],
  },
];

// Quiet light basemap keeps weather overlays and place names easy to read.
const lightMapStyle: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#eef0ed" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#57636c" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#e0e8df" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#d8ddd8" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#cbdde4" }],
  },
];

let loadPromise: Promise<typeof google.maps> | null = null;

export async function loadGoogleMaps(): Promise<typeof google.maps> {
  if (window.google?.maps) {
    return window.google.maps;
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const callbackName = `initGoogleMaps_${Date.now()}`;
      (window as any)[callbackName] = () => {
        delete (window as any)[callbackName];
        if (window.google?.maps) {
          resolve(window.google.maps);
        } else {
          loadPromise = null;
          reject(new Error("Google Maps failed to load"));
        }
      };

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&v=weekly&loading=async&callback=${callbackName}`;
      script.async = true;

      script.onerror = () => {
        delete (window as any)[callbackName];
        loadPromise = null;
        reject(new Error("Failed to load Google Maps script"));
      };

      document.head.appendChild(script);
    });
  }

  return loadPromise;
}

export interface UseGoogleMapOptions {
  containerRef: Ref<HTMLElement | null>;
  initialZoom?: number;
  gestureHandling?: google.maps.MapOptions["gestureHandling"];
  center?: MaybeRefOrGetter<{ lat: number; lng: number } | null | undefined>;
  onReady?: (map: google.maps.Map) => void;
  onIdle?: () => void;
  beforeRecreate?: () => void;
}

export function useGoogleMap(options: UseGoogleMapOptions) {
  const {
    containerRef,
    initialZoom = MAP_ZOOM_DEFAULT,
    gestureHandling = "greedy",
    center: locationCenter,
    onReady,
    onIdle,
    beforeRecreate,
  } = options;

  const map = shallowRef<google.maps.Map | null>(null);
  const isLoading = ref(true);

  const settingsStore = useSettingsStore();
  const { isDarkMode } = storeToRefs(settingsStore);

  let generation = 0;
  async function initMap(opts?: { forceRecreate?: boolean }) {
    const forceRecreate = opts?.forceRecreate ?? false;
    if (!containerRef.value) return;
    if (map.value && !forceRecreate) return;

    const run = ++generation;
    await loadGoogleMaps();
    if (run !== generation || !containerRef.value) return;

    // Allow components to clean up overlays before map is recreated
    if (forceRecreate && map.value) {
      beforeRecreate?.();
    }

    // Preserve current center/zoom if recreating
    const currentCenter = map.value?.getCenter();
    const currentZoom = map.value?.getZoom();

    const selectedCenter = toValue(locationCenter);
    const center = currentCenter
      ? { lat: currentCenter.lat(), lng: currentCenter.lng() }
      : selectedCenter
        ? { lat: selectedCenter.lat, lng: selectedCenter.lng }
        : US_CENTER;
    const zoom = currentZoom ?? initialZoom;

    const isDark = isDarkMode.value;
    map.value = new google.maps.Map(containerRef.value, {
      center,
      zoom,
      styles: isDark ? darkMapStyle : lightMapStyle,
      disableDefaultUI: true,
      zoomControl: false,
      gestureHandling,
      backgroundColor: isDark ? "#0d1117" : "#f5f5f5",
    });

    isLoading.value = false;

    if (onIdle) {
      map.value.addListener("idle", onIdle);
    }

    onReady?.(map.value);
  }

  function zoomIn() {
    if (!map.value) return;
    map.value.setZoom((map.value.getZoom() || MAP_ZOOM_FALLBACK) + 1);
  }

  function zoomOut() {
    if (!map.value) return;
    map.value.setZoom((map.value.getZoom() || MAP_ZOOM_FALLBACK) - 1);
  }

  function setCenter(lat: number, lng: number, zoom?: number) {
    if (!map.value) return;
    map.value.setCenter({ lat, lng });
    if (zoom !== undefined) {
      map.value.setZoom(zoom);
    }
  }

  function panTo(lat: number, lng: number) {
    if (!map.value) return;
    map.value.panTo({ lat, lng });
  }

  function fitBounds(
    bounds: google.maps.LatLngBounds,
    padding?: number | google.maps.Padding,
  ) {
    if (!map.value) return;
    map.value.fitBounds(bounds, padding);
  }

  function cleanup() {
    generation++;
    if (map.value) {
      google.maps.event.clearInstanceListeners(map.value);
    }
    map.value = null;
  }

  // Read the latest coordinates after Maps loads, and follow later location changes.
  // Watching coordinates (not location object identity) leaves user panning alone
  // when reverse geocoding or a refresh only updates metadata.
  watch(
    () => {
      const location = toValue(locationCenter);
      return [location?.lat, location?.lng] as const;
    },
    ([lat, lng], [previousLat, previousLng]) => {
      if (!map.value || lat === undefined || lng === undefined) return;
      if (lat === previousLat && lng === previousLng) return;
      map.value.panTo({ lat, lng });
      if ((map.value.getZoom() ?? 0) < MAP_ZOOM_MIN_RECENTER) {
        map.value.setZoom(MAP_ZOOM_MIN_RECENTER);
      }
    },
  );

  // Appearance is presentation only: preserve map position, overlays and playback.
  watch(isDarkMode, (dark) => {
    map.value?.setOptions({
      styles: dark ? darkMapStyle : lightMapStyle,
      backgroundColor: dark ? "#0d1117" : "#f5f5f5",
    });
  });

  return {
    map,
    isLoading,
    initMap,
    zoomIn,
    zoomOut,
    setCenter,
    panTo,
    fitBounds,
    cleanup,
  };
}
